import * as React from 'react'
import { CalendarIcon, Clock, X } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { logger } from '../utils/logger'
import type { DateRange, Matcher } from 'react-day-picker'
import { Calendar } from '@/components/ui/calendar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export type DateTimeRange = DateRange & {
  fromTime?: string // HH:mm format
  toTime?: string // HH:mm format
}

type DateRangeFilterProps = {
  value?: DateTimeRange
  placeholder?: string
  onChange: (value: DateTimeRange | undefined) => void
  /** Enable optional time inputs */
  enableTime?: boolean
  /** Disable dates after this date (defaults to today) */
  maxDate?: Date
  /** Disable dates before this date */
  minDate?: Date
}

const formatDate = (value: Date) => value.toLocaleDateString()

const formatDateTime = (date: Date, time?: string) => {
  if (!time) return formatDate(date)
  return `${formatDate(date)} ${time}`
}

export function DateRangeFilter({
  value,
  placeholder,
  onChange,
  enableTime = true,
  maxDate,
  minDate,
}: DateRangeFilterProps) {
  const { t: tUi } = useTranslation('ui')
  const [open, setOpen] = React.useState(false)
  const hasValue = !!(value?.from || value?.to)
  const hasTime = !!(value?.fromTime || value?.toTime)

  const label = value?.from
    ? value.to
      ? `${formatDateTime(value.from, value.fromTime)} – ${formatDateTime(value.to, value.toTime)}`
      : formatDateTime(value.from, value.fromTime)
    : (placeholder ?? tUi('filters.dateRange'))

  const disabledDays = React.useMemo<Array<Matcher> | undefined>(() => {
    const matchers: Array<Matcher> = []
    if (minDate) matchers.push({ before: minDate })
    if (maxDate) matchers.push({ after: maxDate })
    return matchers.length ? matchers : undefined
  }, [minDate, maxDate])

  const handleSelect = (next: DateRange | undefined) => {
    logger.debug('[DateRangeFilter] onSelect', { next })
    // Preserve existing time values when dates change
    if (next) {
      onChange({
        ...next,
        fromTime: value?.fromTime,
        toTime: value?.toTime,
      })
    } else {
      onChange(undefined)
    }
  }

  const handleFromTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = e.target.value || undefined
    logger.debug('[DateRangeFilter] fromTime change', { time })
    onChange({
      ...value,
      from: value?.from,
      to: value?.to,
      fromTime: time,
      toTime: value?.toTime,
    })
  }

  const handleToTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = e.target.value || undefined
    logger.debug('[DateRangeFilter] toTime change', { time })
    onChange({
      ...value,
      from: value?.from,
      to: value?.to,
      fromTime: value?.fromTime,
      toTime: time,
    })
  }

  const handleClearTime = () => {
    logger.debug('[DateRangeFilter] clear time')
    onChange({
      ...value,
      from: value?.from,
      to: value?.to,
      fromTime: undefined,
      toTime: undefined,
    })
  }

  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="justify-start">
            <CalendarIcon className="mr-2 h-4 w-4" />
            <span className="truncate max-w-[200px]">{label}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={value}
            onSelect={handleSelect}
            numberOfMonths={2}
            captionLayout="dropdown"
            fromYear={2020}
            toYear={maxDate?.getFullYear() ?? 2030}
            defaultMonth={maxDate ?? new Date()}
            disabled={disabledDays}
          />
          {enableTime && value?.from && (
            <div className="border-t p-3">
              <div className="flex items-center gap-1 mb-2 text-sm text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>{tUi('filters.timeOptional')}</span>
                {hasTime && (
                  <button
                    type="button"
                    className="ml-auto text-xs hover:text-foreground"
                    onClick={handleClearTime}
                  >
                    {tUi('filters.clear')}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor="from-time"
                    className="text-xs text-muted-foreground"
                  >
                    {tUi('filters.from')}
                  </Label>
                  <Input
                    id="from-time"
                    type="time"
                    value={value.fromTime ?? ''}
                    onChange={handleFromTimeChange}
                    className="w-[100px] h-8 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor="to-time"
                    className="text-xs text-muted-foreground"
                  >
                    {tUi('filters.to')}
                  </Label>
                  <Input
                    id="to-time"
                    type="time"
                    value={value.toTime ?? ''}
                    onChange={handleToTimeChange}
                    className="w-[100px] h-8 text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
      {hasValue ? (
        <button
          type="button"
          aria-label={tUi('filters.clearDateRange')}
          className="text-muted-foreground hover:text-foreground"
          onClick={() => {
            logger.debug('[DateRangeFilter] clear clicked')
            onChange(undefined)
          }}
        >
          <X className="w-4 h-4" />
        </button>
      ) : null}
    </div>
  )
}
